import {
  CDNWidgetSettings,
  CDNStoreSettings,
  CDNWidgetSettingsResource,
  CDNProductsAndSettings,
  CDNProductAndSettings,
  CDNProductsAndSettingsResource,
  CDNBundleSettings,
  CDNProductKeyObject,
  CDNProductRaw,
  CDNProductType,
  CDNProductQuery_2020_12,
  CDNProductQuery_2022_06,
  CDNProductResponseType,
} from '../types';
import { productArrayMapper, productMapper, widgetSettingsMapper } from '../mappers/cdn';
import { cdnRequest } from '../utils/request';

const BASE_CDN_VERSION = '2020-12';

// Default store settings if none are provided from the cdn
const DEFAULT_STORE_SETTINGS: CDNStoreSettings = {
  store_currency: {
    currency_code: 'USD',
    currency_symbol: '$',
    decimal_separator: '.',
    thousands_separator: ',',
    currency_symbol_location: 'left',
  },
};

const promiseCache = new Map();

// This will cache the request and use the same promise anywhere we call this function. This will never make multiple calls and always return the first request
function cacheRequest<T>(cacheKey: string, request: () => T): T {
  if (!promiseCache.has(cacheKey)) {
    promiseCache.set(cacheKey, request());
  }
  return promiseCache.get(cacheKey);
}

export async function getCDNProduct<
  T extends CDNProductQuery_2020_12 | CDNProductQuery_2022_06 = CDNProductQuery_2020_12
>(externalProductId: string | number, query?: T): Promise<CDNProductType<T>> {
  if (externalProductId === undefined || externalProductId === '') {
    throw new Error('ID is required');
  }
  const version = query?.version ?? '2020-12';
  const { product } = await cacheRequest(`product.${externalProductId}.${version}`, () =>
    cdnRequest<CDNProductResponseType<T>>('get', `/product/${version}/${externalProductId}.json`)
  );

  if (version === '2020-12') {
    return productMapper(product as CDNProductRaw) as CDNProductType<T>;
  }
  return product as CDNProductType<T>;
}

export async function getCDNStoreSettings(): Promise<CDNStoreSettings> {
  const storeSettings = await cacheRequest('storeSettings', () =>
    cdnRequest<CDNStoreSettings>('get', `/${BASE_CDN_VERSION}/store_settings.json`).catch(() => {
      return DEFAULT_STORE_SETTINGS;
    })
  );
  return storeSettings;
}

export async function getCDNWidgetSettings(): Promise<CDNWidgetSettings> {
  const { widget_settings } = await cacheRequest('widgetSettings', () =>
    cdnRequest<CDNWidgetSettingsResource>('get', `/${BASE_CDN_VERSION}/widget_settings.json`)
  );

  const widgetSettings = widgetSettingsMapper(widget_settings);

  return widgetSettings;
}

export async function getCDNProductsAndSettings(): Promise<CDNProductsAndSettings> {
  const { products, widget_settings, store_settings, meta } = await cacheRequest('productsAndSettings', () =>
    cdnRequest<CDNProductsAndSettingsResource>('get', `/product/${BASE_CDN_VERSION}/products.json`)
  );

  if (meta?.status === 'error') {
    return Promise.reject(meta.message);
  }

  return {
    products: productArrayMapper(products),
    widget_settings: widgetSettingsMapper(widget_settings),
    store_settings: store_settings ?? {},
  };
}

export async function getCDNProducts(): Promise<CDNProductKeyObject[]> {
  const { products } = await getCDNProductsAndSettings();
  return products;
}

export async function getCDNProductAndSettings(externalProductId: string | number): Promise<CDNProductAndSettings> {
  const [product, store_settings, widget_settings] = await Promise.all([
    getCDNProduct(externalProductId),
    getCDNStoreSettings(),
    getCDNWidgetSettings(),
  ]);

  return {
    product,
    store_settings,
    widget_settings,
    storeSettings: store_settings,
    widgetSettings: widget_settings,
  };
}

export async function getCDNBundleSettings(
  externalProductId: string | number
): Promise<CDNBundleSettings | null | undefined> {
  const { bundle_product } = await getCDNProduct(externalProductId);

  return bundle_product;
}

export async function resetCDNCache() {
  return Array.from(promiseCache.keys()).forEach(key => promiseCache.delete(key));
}
